/**
 * useVoiceChat Hook
 * Integrates voice input/output with the existing chat system.
 *
 * This hook wraps useChat and adds voice capabilities:
 * - Voice input via Whisper transcription
 * - Voice output via TTS synthesis
 * - Automatic silence detection
 *
 * State Flow:
 *   idle ──[start()]──> listening ──[silence]──> processing ──> thinking ──> speaking ──> listening
 *
 * Call stop() at any point to abort and return to idle.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useAudioRecorder } from "./useAudioRecorder";
import { voiceApi } from "../api/client";
import type {
  ConversationState,
  ConversationMode,
  VoiceChatConfig,
  VoiceChatReturn,
} from "../types/voice";

const DEFAULT_MODE: ConversationMode = { input: "voice", output: "voice" };

export function useVoiceChat(config: VoiceChatConfig = {}): VoiceChatReturn {
  const {
    mode: initialMode = DEFAULT_MODE,
    voice = "echo",
    silenceThresholdMs,
    silenceLevel,
    minSpeechDurationMs,
    transcriptionHint,
    onTranscription,
    onResponse,
    onError,
    onStateChange,
  } = config;

  const [state, setState] = useState<ConversationState>("idle");
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setModeState] = useState<ConversationMode>(initialMode);

  const isActiveRef = useRef(false);
  const isProcessingRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const modeRef = useRef<ConversationMode>(initialMode);

  // Keep mode ref in sync
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // Update state and notify
  const updateState = useCallback(
    (newState: ConversationState) => {
      setState(newState);
      onStateChange?.(newState);
    },
    [onStateChange]
  );

  /**
   * Handle captured audio from voice input.
   * Transcribes audio, sends to chat, and optionally plays response.
   */
  const handleAudioCaptured = useCallback(
    async (blob: Blob) => {
      if (!isActiveRef.current) return;
      if (isProcessingRef.current) return;
      if (modeRef.current.input !== "voice") return;

      isProcessingRef.current = true;
      abortControllerRef.current = new AbortController();

      try {
        updateState("processing");

        // Transcribe audio to text
        const text = await voiceApi.transcribe(blob, transcriptionHint);

        if (!text.trim() || !isActiveRef.current) {
          isProcessingRef.current = false;
          if (isActiveRef.current && modeRef.current.input === "voice") {
            updateState("listening");
          }
          return;
        }

        onTranscription?.(text);

        // Return transcribed text - the parent component will handle sending to chat
        // This allows integration with the existing useChat hook

        // If voice output mode, we need to wait for the response and synthesize
        // For now, just notify the transcription is complete
        // The parent ChatInterface will coordinate with useChat

        isProcessingRef.current = false;

        if (isActiveRef.current && modeRef.current.input === "voice") {
          updateState("listening");
        } else {
          updateState("idle");
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          isProcessingRef.current = false;
          return;
        }

        const message = err instanceof Error ? err.message : "Error processing audio";
        setError(message);
        onError?.(err instanceof Error ? err : new Error(message));
        isProcessingRef.current = false;

        if (isActiveRef.current && modeRef.current.input === "voice") {
          updateState("listening");
        } else {
          updateState("idle");
        }
      }
    },
    [transcriptionHint, updateState, onTranscription, onError]
  );

  // Audio recorder
  const {
    audioLevel,
    start: startRecording,
    stop: stopRecording,
  } = useAudioRecorder({
    silenceThresholdMs,
    silenceLevel,
    minSpeechDurationMs,
    onAudioCaptured: handleAudioCaptured,
    onError: (err) => {
      setError(err.message);
      onError?.(err);
    },
  });

  // Start/restart recording when in listening state
  useEffect(() => {
    if (state === "listening" && isActiveRef.current && mode.input === "voice") {
      const timer = setTimeout(() => {
        if (isActiveRef.current && modeRef.current.input === "voice") {
          startRecording();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [state, mode.input, startRecording]);

  /**
   * Synthesize and play response audio.
   * Called by parent component when AI response is received.
   */
  const speakResponse = useCallback(
    async (text: string) => {
      if (modeRef.current.output !== "voice") {
        onResponse?.(text);
        return;
      }

      try {
        updateState("speaking");
        const audioBlob = await voiceApi.synthesize(text, voice);

        if (!isActiveRef.current) {
          return;
        }

        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        await new Promise<void>((resolve) => {
          const cleanup = () => {
            URL.revokeObjectURL(audioUrl);
            audioRef.current = null;
            resolve();
          };

          audio.onended = cleanup;
          audio.onerror = cleanup;
          audio.onpause = cleanup;

          audio.play();
        });

        onResponse?.(text);

        // Return to listening if still active
        if (isActiveRef.current && modeRef.current.input === "voice") {
          updateState("listening");
        } else {
          updateState("idle");
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Speech synthesis failed";
        setError(message);
        onError?.(err instanceof Error ? err : new Error(message));

        if (isActiveRef.current && modeRef.current.input === "voice") {
          updateState("listening");
        } else {
          updateState("idle");
        }
      }
    },
    [voice, updateState, onResponse, onError]
  );

  /**
   * Set state to thinking (called when chat is processing)
   */
  const setThinking = useCallback(() => {
    updateState("thinking");
  }, [updateState]);

  /**
   * Start voice conversation.
   */
  const start = useCallback(() => {
    setError(null);
    isActiveRef.current = true;
    setIsActive(true);

    if (mode.input === "voice") {
      updateState("listening");
    } else {
      updateState("idle");
    }
  }, [mode.input, updateState]);

  /**
   * Stop voice conversation.
   */
  const stop = useCallback(() => {
    isActiveRef.current = false;
    setIsActive(false);

    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    stopRecording();

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    isProcessingRef.current = false;
    updateState("idle");
  }, [updateState, stopRecording]);

  /**
   * Toggle voice conversation.
   */
  const toggle = useCallback(() => {
    if (isActiveRef.current) {
      stop();
    } else {
      start();
    }
  }, [start, stop]);

  /**
   * Interrupt AI speech.
   */
  const interrupt = useCallback(() => {
    if (audioRef.current && state === "speaking") {
      audioRef.current.pause();
      if (mode.input === "voice" && isActiveRef.current) {
        updateState("listening");
      } else {
        updateState("idle");
      }
    }
  }, [state, mode.input, updateState]);

  /**
   * Change mode at runtime.
   */
  const setMode = useCallback(
    (newMode: Partial<ConversationMode>) => {
      const updatedMode = { ...modeRef.current, ...newMode };

      if (modeRef.current.input === "voice" && newMode.input === "text") {
        stopRecording();
        if (state === "listening") {
          updateState("idle");
        }
      }

      if (modeRef.current.input === "text" && newMode.input === "voice" && isActiveRef.current) {
        updateState("listening");
      }

      setModeState(updatedMode);
    },
    [state, stopRecording, updateState]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isActiveRef.current = false;
      abortControllerRef.current?.abort();
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return {
    state,
    isActive,
    audioLevel: mode.input === "voice" ? audioLevel : 0,
    error,
    mode,
    start,
    stop,
    toggle,
    interrupt,
    setMode,
    // Additional methods for integration with useChat
    speakResponse,
    setThinking,
  };
}
